import { Toaster } from "@pos-vidyon/ui/components/sonner";
import { toast } from "sonner";
import type { QueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { HeadContent, Outlet, createRootRouteWithContext, useRouter } from "@tanstack/react-router";
import { ThemeProvider } from "@/components/theme-provider";
import { orpc } from "@/utils/orpc";
import { authClient, sessionQueryOptions } from "@/lib/auth-client";
import { useEffect, useRef } from "react";
import { useTerminalStore } from "@/store/terminal";
import { Electroview } from "electrobun/view";
import { type MyUpdateSchema } from "@/lib/rpc-schema";
import { useUpdateStore } from "@/store/update";
import { ConnectionBanner } from "@/components/connection-banner";
import * as m from "@/paraglide/messages.js";

import "../index.css";

declare global {
  interface Window {
    desktopRpc?: any;
  }
}

export interface RouterAppContext {
  orpc: typeof orpc;
  queryClient: QueryClient;
  session?: Awaited<ReturnType<typeof authClient.getSession>>;
  isDesktop: boolean;
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
  beforeLoad: async ({ context: { queryClient } }) => {
    // If we are offline, don't attempt to refetch the session. Use the cached
    // value if available; otherwise the auth guard will also skip the redirect
    // and rely on the cached state. This prevents deleting the session on the
    // client side simply because the network is unavailable.
    const isOnline = typeof window !== "undefined" ? navigator.onLine : true;
    if (!isOnline) {
      const cached = queryClient.getQueryData(sessionQueryOptions.queryKey);
      if (cached) {
        return { session: cached };
      }
      return {
        session: {
          data: null,
          error: { message: "offline", status: 0 },
          __offline: true,
        } as any,
      };
    }

    try {
      const session = await queryClient.ensureQueryData(sessionQueryOptions);
      return {
        session: session,
      };
    } catch (error) {
      // If the session fetch completely fails (network timeout, DNS failure, etc.),
      // return a marker object so the _auth guard can distinguish this from
      // "genuinely unauthenticated". This prevents kicking users to /login on
      // transient network issues.
      console.warn("[RootRoute] Session fetch failed, likely network issue:", error);
      return {
        session: {
          data: null,
          error: { message: String(error), status: 0 },
          __networkError: true,
        } as any,
      };
    }
  },
  component: RootComponent,
  head: () => ({
    meta: [
      {
        title: "pos-vidyon",
      },
      {
        name: "description",
        content: "pos-vidyon is a web application",
      },
    ],
    links: [
      {
        rel: "icon",
        href: "/favicon.ico",
      },
    ],
  }),
});

function RootComponent() {
  const { data: sessionData } = useQuery(sessionQueryOptions);
  const isImpersonated = !!sessionData?.data?.session?.impersonatedBy;
  const router = useRouter();
  const { queryClient, isDesktop } = Route.useRouteContext();
  const { deviceId, restaurantCode, restaurantName, userCode, resetTerminal } = useTerminalStore();
  const updateState = useUpdateStore();
  const rpcRef = useRef<any>(null);

  useEffect(() => {
    // 1. Webview Sentry Context
    Sentry.setUser({ id: deviceId, username: userCode || undefined });
    Sentry.setTags({
      restaurantCode: restaurantCode || undefined,
      restaurantName: restaurantName || undefined,
      platform: isDesktop ? "desktop" : "web",
    });

    // 2. Bun Process Sentry Context
    if (isDesktop && window.desktopRpc) {
      console.log("[Webview] Syncing updated Sentry context to Bun:", {
        deviceId,
        restaurantCode,
        restaurantName,
        userCode,
      });
      window.desktopRpc.send.setSentryContext({
        deviceId,
        restaurantCode,
        restaurantName,
        userCode,
      });
    }
  }, [deviceId, restaurantCode, restaurantName, userCode, isDesktop]);

  useEffect(() => {
    if (!isDesktop) return;

    // Define webview RPC handlers for incoming messages from Bun
    const webviewRpc = Electroview.defineRPC<MyUpdateSchema>({
      maxRequestTime: 30000,
      handlers: {
        requests: {},
        messages: {
          updateStatus: (payload) => {
            console.log("[UpdateStatus] Message from Bun process:", payload);
            updateState.setStatus(payload.status, payload.message, {
              progress: payload.progress,
              bytesDownloaded: payload.bytesDownloaded,
              totalBytes: payload.totalBytes,
              error: payload.error,
            });
          },
        },
      },
    });

    // Initialize Electroview
    new Electroview({ rpc: webviewRpc });
    rpcRef.current = webviewRpc;
    window.desktopRpc = webviewRpc;

    // Sync Sentry context immediately on startup
    const terminalState = useTerminalStore.getState();
    console.log("[Webview] Syncing initial Sentry context to Bun on startup");
    webviewRpc.send.setSentryContext({
      deviceId: terminalState.deviceId,
      restaurantCode: terminalState.restaurantCode,
      restaurantName: terminalState.restaurantName,
      userCode: terminalState.userCode,
    });

    const checkUpdates = async () => {
      try {
        updateState.setChecking(true);
        console.log("[UpdateStatus] Checking for updates...");
        const res = await webviewRpc.request.checkForUpdate();
        console.log("[UpdateStatus] Update check response:", res);
        updateState.setUpdateInfo(res);
      } catch (err: any) {
        console.error("[UpdateStatus] Error checking for updates:", err);
      } finally {
        updateState.setChecking(false);
      }
    };

    // Run check immediately
    checkUpdates();

    // Check for updates every 10 seconds for the demo
    const interval = setInterval(checkUpdates, 10000);

    return () => {
      clearInterval(interval);
      rpcRef.current = null;
      window.desktopRpc = undefined;
    };
  }, [isDesktop]);

  const handleUpdate = async () => {
    if (!rpcRef.current) return;
    try {
      updateState.setDownloading(true);
      await rpcRef.current.request.downloadUpdate();
    } catch (err: any) {
      console.error("[UpdateStatus] Failed to start update download:", err);
      updateState.setDownloading(false);
    }
  };

  const handleApply = async () => {
    if (!rpcRef.current) return;
    try {
      await rpcRef.current.request.applyUpdate();
    } catch (err: any) {
      console.error("[UpdateStatus] Failed to apply update:", err);
    }
  };

  const { data: terminalStatus } = useQuery({
    ...orpc.checkTerminalStatus.queryOptions({ input: { deviceId } }),
    enabled: isDesktop && !!restaurantCode && !!deviceId,
    refetchInterval: 20000,
  });

  useEffect(() => {
    if (!terminalStatus || terminalStatus.active) return;

    const deactivate = async () => {
      console.warn(
        "[RootComponent] Terminal has been deactivated/deleted from admin panel. Resetting...",
      );
      toast.error(m.auth_terminalDeactivated());
      resetTerminal();
      await authClient.signOut();
      queryClient.clear();
      await router.navigate({ to: "/login" });
    };

    deactivate();
  }, [terminalStatus, resetTerminal, router, queryClient]);

  const showUpdateBanner = isDesktop && updateState.updateAvailable;

  return (
    <>
      <HeadContent />
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        disableTransitionOnChange
        storageKey="vite-ui-theme"
      >
        <div className="flex flex-col h-screen overflow-hidden">
          <ConnectionBanner />
          {showUpdateBanner && (
            <div className="bg-gradient-to-r from-blue-700 to-indigo-800 text-white px-4 py-3 text-sm font-medium flex items-center justify-between gap-4 z-50 shrink-0 border-b border-indigo-900 shadow-lg transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="p-1 bg-white/10 rounded-full animate-pulse">
                  <svg
                    className="w-5 h-5 text-indigo-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </div>
                <div>
                  <span className="font-semibold text-white">{m.updates_newUpdateAvailable()}</span>{" "}
                  <span className="text-indigo-100">
                    {updateState.updateReady
                      ? m.updates_updateReady()
                      : updateState.downloading
                        ? m.updates_downloadingUpdate({
                            progress: updateState.progress,
                            message: updateState.message || m.common_loading(),
                          })
                        : m.updates_checkingUpdate()}
                  </span>
                  {updateState.error && (
                    <span className="text-rose-200 block text-xs mt-0.5">
                      {m.updates_updateError({ error: updateState.error })}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {updateState.updateReady ? (
                  <button
                    onClick={handleApply}
                    className="bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all shadow hover:shadow-lg flex items-center gap-1.5 cursor-pointer"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89"
                      />
                    </svg>
                    {m.updates_restart()}
                  </button>
                ) : updateState.downloading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-white/20 h-2 rounded-full overflow-hidden hidden sm:block">
                      <div
                        className="bg-emerald-400 h-full rounded-full transition-all duration-300"
                        style={{ width: `${updateState.progress}%` }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-indigo-200">
                      %{updateState.progress}
                    </span>
                  </div>
                ) : (
                  <button
                    onClick={handleUpdate}
                    disabled={updateState.downloading}
                    className="bg-white text-indigo-700 hover:bg-indigo-50 active:bg-indigo-100 disabled:opacity-50 px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all shadow hover:shadow-lg flex items-center gap-1.5 cursor-pointer"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    {m.updates_updateNow()}
                  </button>
                )}
              </div>
            </div>
          )}
          {isImpersonated && sessionData?.data && (
            <div className="bg-amber-600 text-white px-4 py-2.5 text-center text-sm font-semibold flex items-center justify-center gap-4 z-50 shrink-0 border-b border-amber-500 shadow-md">
              <span>
                {m.impersonation_actingAs({
                  name: sessionData.data.user.name,
                  email: sessionData.data.user.email,
                })}
              </span>
              <button
                onClick={async () => {
                  try {
                    await authClient.admin.stopImpersonating();
                    // Clear TanStack Query cache and navigate to superadmin natively.
                    // This prevents fetch aborts and raw location reloads.
                    queryClient.clear();
                    await router.navigate({ to: "/superadmin" });
                  } catch (err: any) {
                    console.error("[RootComponent] stopImpersonating error:", err);
                    toast.error(err.message || "Failed to stop impersonation");
                  }
                }}
                className="bg-white text-amber-700 px-3 py-1 rounded-md text-xs hover:bg-amber-50 transition-colors font-bold uppercase tracking-wider"
              >
                {m.impersonation_stopImpersonation()}
              </button>
            </div>
          )}
          <div className="flex-1 overflow-hidden">
            <Outlet />
          </div>
        </div>
        <Toaster richColors />
      </ThemeProvider>
    </>
  );
}
